import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { emptyBoard, emptyTray } from './boardUtil';
import tileBackgroundEnum from './tileBackgroundEnum';
import messageEnum from './messageEnum';
import { play, soundTypes } from '../../util/sound';
import { getTimer } from '../../util/timer';

const placeCharHelper = (state, pos, char) => {
  switch (pos.type) {
    case 'board':
      state.currentCharacters[pos.y][pos.x] = char;
      break;
    case 'tray':
      state.tray[pos.x] = char;
      break;
    default:
      throw Error('Got '+pos.type);
  }
};

const cleanCurrentTile = (state) => {
  for (let y = 0; y < state.board.length; y++) {
    for (let x = 0; x < state.board[0].length; x++) {
      let char = state.currentCharacters[y][x];
      if (char !== null) {
        state.currentCharacters[y][x] = null;
      }
    }
  }
};

const resetGameHelper = (state) => {
  state.board = emptyBoard(0, 0);
  state.playedCharacters = emptyBoard(0, 0);
  state.currentCharacters = emptyBoard(0, 0);
  state.markedCharacters = emptyBoard(0, 0);
  state.tray = emptyTray();
  state.defLetters = {};
  state.players = {};
  state.tilesLeft = 0;
  state.currentPlayerNo = null;
  state.localPlayerNo = null;
  state.log = [];
  state.paused = false;
  state.wait = true;
  state.ended = false;
  state.startDataSent = false;
}

const firstInTrayHelper = (state, lambda) => {
  for (let x in state.tray) {
    if (lambda(state.tray[x])) {
      return x;
    }
  }
  return null;
}

const charAtPosHelper = (state, pos) => {
  switch (pos.type) {
    case 'board':
      return state.currentCharacters[pos.y][pos.x];
    case 'tray':
      return state.tray[pos.x];
    default:
      return null;
  }
};

function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}

export const gameSlice = createSlice({
  name: 'game',
  initialState: {
    inGame: false,
    ended: false,
    board: emptyBoard(0, 0),
    playedCharacters: emptyBoard(0, 0),
    currentCharacters: emptyBoard(0, 0),
    markedCharacters: emptyBoard(0, 0),
    tray: [],
    defLetters: {},
    players: {},
    tilesLeft: 0,
    currentPlayerNo: null,
    localPlayerNo: null,
    log: [],
    paused: false,
    wait: false,
    startDataSent: false,
  },
  reducers: {
    setInGame: (state, action) => {
      resetGameHelper(state);
      const payload = action.payload;
      state.inGame = payload;
      if (!payload) {
        getTimer().cancel('game');
      }
    },
    setBoard: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const board = action.payload;
      state.board = board;
      state.playedCharacters = emptyBoard(board.length, board[0].length);
      state.currentCharacters = emptyBoard(board.length, board[0].length);
      state.markedCharacters = emptyBoard(board.length, board[0].length);
      state.tray = emptyTray();
      state.defLetters = {};
      state.players = {};
      state.currentPlayerNo = null;
      state.localPlayerNo = null;
      state.wait = true;
      state.paused = false;
      state.ended = false;
      state.startDataSent = false;
    },
    setDefLetters: (state, action) => {
      if (!state.inGame) {
        return;
      }

      for (const c of action.payload) {
        state.defLetters[c.char] = c.points;
      }
    },
    setPlayers: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;

      let players = {};
      for (const p of payload) {
        players[p.no] = p;
        if (p.local) {
          state.localPlayerNo = p.no;
        }
      }
      state.players = players;
    },
    turn: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      const player = state.players[payload.no];

      // If this is the first turn in the game set all players tilesLeft and total time
      if (player.tilesLeft === null) {
        for (const no in state.players) {
          state.players[no] = {...state.players[no], tilesLeft: payload.bleft, totalTimeLeft: payload.totalTime};
        }
      }

      // BLEFT is for the previous player. So we need to figure out the previousNo.
      let numPlayers = Object.values(state.players).length;
      let previousNo = (payload.no - 1 + numPlayers) % numPlayers;

      state.players[payload.no] = {...state.players[payload.no], turnTimeLeft: payload.turnTime - 1, totalTimeLeft: payload.totalTime - 1};
      state.players[previousNo] = {...state.players[previousNo], tilesLeft: payload.bleft};

      state.tilesLeft = payload.left;
      state.currentPlayerNo = payload.no;
      state.wait = false;

      state.log.push({type: messageEnum.TURN, args: {no: payload.no}});

      if (state.startDataSent) {
        play(soundTypes.NEW_TURN);
      }
      getTimer().start('game', 1000);
    },
    timeSync: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;

      let turnTimeLeft = payload.turnTime;
      let totalTimeLeft = payload.totalTime;
      if (payload.no === state.currentPlayerNo) {
        turnTimeLeft -= 1;
        totalTimeLeft -= 1;
        if (!state.paused) {
          getTimer().start('game', 1000);
        }
      }
      const player = state.players[payload.no];
      player.turnTimeLeft = turnTimeLeft;
      player.totalTimeLeft = totalTimeLeft;
    },
    wait: (state, action) => {
      state.wait = action.payload;
    },
    playedWords: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      if (!payload.ok) {
        state.log.push({type: messageEnum.INCORRECT_WORDS, args: {words: payload.words}});
        state.log.push({type: messageEnum.N_TRIES_LEFT, args: {triesLeft: payload.triesLeft}});
        state.wait = false;
        return;
      }

      // Remove current tiles. The server will send us the played tile
      if (state.localPlayerNo === payload.no) {
        cleanCurrentTile(state);
      }

      state.log.push({type: messageEnum.WORDS, args: {no: payload.no, words: payload.words}});

      const totalPoints = payload.words.map(w => w.points).reduce((a, b) => a + b, 0);
      state.players[payload.no].points += totalPoints;
    },
    message: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.log.push({type: messageEnum.MESSAGE, args: {no: payload.no, data: payload.data}});
    },
    swapped: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;

      if (state.localPlayerNo === payload.no) {
        cleanCurrentTile(state);
      }

      state.log.push({type: messageEnum.EXCHANGE_LETTERS, args: {no: payload.no, count: payload.number}});
      state.wait = false;
    },
    joinAfterDrop: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.log.push({type: messageEnum.RECONNECTED, args: {no: payload.no}});
    },
    dropNotFin: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.log.push({type: messageEnum.DISCONNECTED, args: {no: payload.no}});
    },
    timeExpired: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.log.push({type: messageEnum.TIMEOUT, args: {no: payload.no}});
    },
    passed: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.log.push({type: messageEnum.PASSED, args: {no: payload.no}});

      state.wait = false;
    },
    pause: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      if (payload.request) {
        state.log.push({type: messageEnum.PAUSE_REQUEST, args: {no: payload.no, choice: payload.choice}});
      } else {
        state.log.push({type: messageEnum.PAUSE_RESPONSE, args: {no: payload.no, choice: !state.paused, accept: payload.accept}});
      }
    },
    pauseState: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.paused = payload.paused;
      if (payload.paused) {
        state.log.push({type: messageEnum.PAUSED});
        getTimer().cancel('game');
      } else {
        state.log.push({type: messageEnum.UNPAUSED});
        getTimer().start('game', 1000);
      }
    },
    spyJoin: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.log.push({type: messageEnum.SPY_JOINED, args: {name: payload.name}});
    },
    spyPart: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.log.push({type: messageEnum.SPY_PARTED, args: {name: payload.name}});
    },
    badPlay: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      switch (payload) {
        case 'BOGUS_CHARS_GIVEN':
          state.log.push({type: messageEnum.BOGUS_CHARS_GIVEN});
          break;
        case 'NOT_ALLOWED_TO_SWAP':
          state.log.push({type: messageEnum.NOT_ALLOWED_TO_SWAP});
          break;
        case 'OCCUPIED_SQUARE':
          state.log.push({type: messageEnum.OCCUPIED_SQUARE});
          break;
        case 'START_IN_THE_MIDDLE':
          state.log.push({type: messageEnum.START_IN_THE_MIDDLE});
          break;
        case 'ONE_CHAR_WORD_NOT_ALLOWED':
          state.log.push({type: messageEnum.ONE_CHAR_WORD_NOT_ALLOWED});
          break;
        case 'NOT_ATTACHED_CHAR':
          state.log.push({type: messageEnum.NOT_ATTACHED_CHAR});
          break;
        case 'NOT_IN_LINE':
          state.log.push({type: messageEnum.NOT_IN_LINE});
          break;
        case 'WE_GOT_GAPS':
          state.log.push({type: messageEnum.WE_GOT_GAPS});
          break;
        case 'PAUSE_IN_PROGRESS':
          state.log.push({type: messageEnum.PAUSE_IN_PROGRESS});
          break;
        default:
          throw Error('Unknown badPlay: '+payload);
      }
      state.wait = false;
    },
    missedTries: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.log.push({type: messageEnum.MISSED_TRIES, args: {no: payload.no}});
    },
    gaveup: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.log.push({type: messageEnum.GAVEUP, args: {no: payload.no}});
    },
    endGame: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;

      for (const p of payload.players) {
        let points = p.score - (p.timeScore ?? 0) + p.charScore;
        state.players[p.no].points = points;
        if (p.charScore > 0) {
          state.players[p.no].tilesLeft = 0;
        }
        state.log.push({type: messageEnum.ENDGAME_SCORE, args: {no: p.no, score: p.score, timeScore: p.timeScore, charScore: p.charScore}});
        if (p.rating !== null && p.oldRating !== null) {
          state.log.push({type: messageEnum.ENDGAME_RATING, args: {no: p.no, rating: p.rating, oldRating: p.oldRating}});
        }
      }

      if (payload.winner !== null) {
        state.log.push({type: messageEnum.ENDGAME_WINNER, args: {no: payload.winner}});
      } else if (payload.dropped !== null) {
        state.log.push({type: messageEnum.ENDGAME_DROP, args: {no: payload.dropped}});
      } else if (state.players.length > 1) {
        state.log.push({type: messageEnum.ENDGAME_DRAW});
      }

      state.currentPlayerNo = null;
      state.ended = true;
      play(soundTypes.ENDSOUND);
      getTimer().cancel('game');
    },
    gameEndedAbrupt: (state, action) => {
      if (!state.inGame) {
        return;
      }

      state.log.push({type: messageEnum.GAME_ENDED_ABRUPT});

      state.currentPlayerNo = null;
      state.ended = true;
      play(soundTypes.ENDSOUND);
      getTimer().cancel('game');
    },
    tryToPlayEmptyJoker: (state, action) => {
      if (!state.inGame) {
        return;
      }

      state.log.push({type: messageEnum.PLAYING_EMPTY_JOKER});
    },
    startDataSent: (state, action) => {
      if (!state.inGame) {
        return;
      }

      state.startDataSent = true;
      if (state.currentPlayerNo !== null) {
        play(soundTypes.NEW_TURN);
      }
    },
    rolled: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      state.log.push({type: messageEnum.ROLLED, args: {no: payload.no, points: payload.points}});
    },
    placeChar: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      const pos = payload.pos;
      placeCharHelper(state, pos, payload.char);
    },
    removeChar: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      const pos = payload.pos;
      placeCharHelper(state, pos, null);
    },
    setJoker: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      const pos = payload.pos;

      placeCharHelper(state, pos, {data: payload.letter, joker: true});
    },
    moveChar: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      const pos = payload.pos;
      const newPos = payload.newPos;

      let oldChar = charAtPosHelper(state, newPos);
      placeCharHelper(state, pos, oldChar);
      placeCharHelper(state, newPos, payload.char);
    },
    clickTileBackground: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const x = firstInTrayHelper(state, c => c !== null);
      if (x === null) {
        return;
      }

      const char = state.tray[x];
      placeCharHelper(state, {type: 'tray', x}, null);
      placeCharHelper(state, action.payload.pos, char);
    },
    clickTile: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;
      const pos = payload.pos;
      const char = charAtPosHelper(state, pos);
      if (char === null) {
        return;
      }

      const x = firstInTrayHelper(state, c => c === null);
      if (x === null) {
        return;
      }

      placeCharHelper(state, pos, null);
      placeCharHelper(state, {type: 'tray', x}, char);
    },
    keypress: (state, action) => {
      const payload = action.payload;
      const pos = payload.pos;
      const char = payload.char.toUpperCase();

      if ((state.defLetters[char] ?? null) === null) {
        return;
      }

      const oldChar = charAtPosHelper(state, pos);
      let x = firstInTrayHelper(state, c => c !== null && c.data === char && !c.joker);
      if (x === null) {
        x = firstInTrayHelper(state, c => c !== null && c.data === char && c.joker);
        if (x === null) {
          // If we can't find the character in the tray but we mouse is over a joker then change
          // the current tile
          if (oldChar !== null && oldChar.joker) {
            oldChar.data = char;
            placeCharHelper(state, pos, oldChar);
            return;
          }

          // Otherwise try to find if there is any joker and we change that one.
          x = firstInTrayHelper(state, c => c !== null && c.joker);
          if (x === null) {
            return;
          }
        }
      }

      const newChar = state.tray[x];
      if (newChar.data !== char && newChar.joker) {
        newChar.data = char;
      }

      placeCharHelper(state, pos, newChar);
      placeCharHelper(state, {type: 'tray', x}, oldChar);
    },
    newTiles: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;

      let x = 0;
      for (const c of payload) {
        for (; x < state.tray.length; x++) {
          if (state.tray[x] === null) {
            placeCharHelper(state, {type: 'tray', x}, c.char);
            break;
          }
        }
      }
    },
    playedChars: (state, action) => {
      if (!state.inGame) {
        return;
      }

      const payload = action.payload;

      // Reset marked chars
      for (let y in state.markedCharacters) {
        for (let x in state.markedCharacters[y]) {
          if (state.markedCharacters[y][x] === true) {
            state.markedCharacters[y][x] = null;
          }
        }
      }

      // Update marked chars
      // First we mark the played characters
      for (const c of payload) {
        const x = c.pos.x;
        const y = c.pos.y;
        state.playedCharacters[y][x] = c.char;
        state.markedCharacters[y][x] = true;
      }

      // Then we mark all the words that were created
      for (const c of payload) {
        const x = c.pos.x;
        const y = c.pos.y;

        const directions_to_check = [
          {
            startX: x - 1,
            startY: y,
            diffX: -1,
            diffY: 0,
            check: (x, y) => x >= 0,
          },
          {
            startX: x + 1,
            startY: y,
            diffX: 1,
            diffY: 0,
            check: (x, y) => x < state.board[y].length,
          },
          {
            startX: x,
            startY: y - 1,
            diffX: 0,
            diffY: -1,
            check: (x, y) => y >= 0,
          },
          {
            startX: x,
            startY: y + 1,
            diffX: 0,
            diffY: 1,
            check: (x, y) => y < state.board.length,
          },
        ];

        for (let c of directions_to_check) {
          for (let dx = c.startX, dy = c.startY; c.check(dx, dy); dx += c.diffX, dy += c.diffY) {
            if (state.markedCharacters[dy][dx]) {
              // If the character is already marked we can bail early
              break;
            }
            if (state.playedCharacters[dy][dx]) {
              state.markedCharacters[dy][dx] = true;
            } else {
              break;
            }  
          }
        }
      }
    },
    shuffleTiles: (state, action) => {
      if (!state.inGame) {
        return;
      }

      let array = [];
      for (let t_x = 0, a_x = 0; t_x < state.tray.length; t_x++) {
        if (state.tray[t_x] != null) {
          array[a_x++] = state.tray[t_x];
           state.tray[t_x] = null;
        }
      }
      shuffle(array);
      for (let x = 0 ; x < array.length; x++) {
        state.tray[x] = array[x];
      }
    },
    cleanTiles: (state, action) => {
      if (!state.inGame) {
        return;
      }

      let trayX = 0;
      for (let y = 0; y < state.board.length; y++) {
        for (let x = 0; x < state.board[0].length; x++) {
          let char = state.currentCharacters[y][x];
          if (char !== null) {
            for (; trayX < state.tray.length; trayX++) {
              if (state.tray[trayX] === null) {
                state.tray[trayX] = char;
                state.currentCharacters[y][x] = null;
                break;
              }
            }
          }
        }
      }
    },
    tick: (state, action) => {
      if (!state.inGame || state.currentPlayerNo === null) {
        return;
      }

      const player = state.players[state.currentPlayerNo];
      if (player.turnTimeLeft > 0) {
        player.turnTimeLeft--;
        player.totalTimeLeft--;
      }
    }
  },
});

export const { setBoard, placeChar, removeChar, playedChar, moveChar, clickTile, clickTileBackground, keypress, newTiles, cleanTiles, shuffleTiles } = gameSlice.actions;

export const getSize = (state) => {
  return {
    height: state.game.board.length,
    width: (state.game.board[0] ?? []).length,
  }
};

export const getCharPoints = (state, prop) => {
  if (prop.char.joker) {
    return 0;
  }
  return state.game.defLetters[prop.char.data] ?? null;
}

export const getTile = (state, prop) => {
  switch (prop.type) {
    case 'board': {
      return {
        marked: state.game.markedCharacters[prop.y][prop.x] ?? null,
        currentChar: state.game.currentCharacters[prop.y][prop.x] ?? null,
        playedChar: state.game.playedCharacters[prop.y][prop.x] ?? null,
        boardTile: state.game.board[prop.y][prop.x],
      };
    }
    case 'tray': {
      return {
        marked: null,
        currentChar: state.game.tray[prop.x] ?? null,
        playedChar: null,
        boardTile: tileBackgroundEnum.NONE,
      };
    }
    default:
      throw Error('Got '+prop.type);
  }
};

export const getCurrentChars = (state) => {
  let chars = [];
  for (let y = 0; y < state.game.board.length; y++) {
    for (let x = 0; x < state.game.board[0].length; x++) {
      let char = state.game.currentCharacters[y][x];
      if (char !== null) {
        chars.push({char, x, y});
      }
    }
  }
  return chars;
}

export const getExistsCurrentChars = createSelector(
  [getCurrentChars],
  (chars) => {
    return !!chars.length;
  }
);

export const getPlayer = (state, no) => state.game.players[no] ?? null;

export const getPlayerProperty = (state, no, prop) => {
  const player = state.game.players[no] ?? null;
  if (!player) {
    return null;
  }
  return player[prop] ?? null;
}

export const getPlayerNOsSorted = createSelector(
  [(state) => state.game.players],
  (players) => {
    return Object.values(players).sort((a, b) => { return b.no - a.no; }).map((p) => p.no);
  }
);

export const getLocalPlayerNo = (state) => state.game.localPlayerNo;

export const getCurrentPlayerNo = (state) => state.game.currentPlayerNo;

export const getCurrentPlayer = (state) => {
  return getPlayer(state, state.game.currentPlayerNo);
};

export const numLogMessages = (state) => state.game.log.length;

export const getLogMessage = (state, id) => state.game.log[id];

export const getTilesLeft = (state) => state.game.tilesLeft;

export const getDefLetters = createSelector(
  [
    (state) => state.game.defLetters,
    (state) => state.init.connectionArgs.locale,
  ],
  (defLetters, locale) => {
    let letters = Object.keys(defLetters);
    letters = letters.sort((a, b) => a.localeCompare(b, locale));
    letters.unshift('');
    return letters;
  }
);

export const getCanPlayTiles = createSelector(
  [
    (state) => state.game.wait,
    (state) => state.game.currentPlayerNo,
    (state) => state.game.localPlayerNo,
  ],
  (wait, currentPlayerNo, localPlayerNo) => {
    if (wait) {
      return false;
    }
    return (currentPlayerNo !== null && currentPlayerNo === localPlayerNo);
  }
);

export const getCanMoveTiles = (state) => !state.game.wait && !state.game.ended && state.game.localPlayerNo !== null;

export const getLoading = (state) => !state.game.startDataSent;
export const inGame = (state) => state.game.inGame;
export const getEnded = (state) => state.game.ended;
export const getPaused = (state) => state.game.paused;
export const getShowLeave = (state) => state.game.ended || state.game.localPlayerNo === null;

export const getGameTabState = (state) => {
  if (inGame(state)) {
    return getLoading(state) ? 2 : 1;
  }
  return 0;
}

export default gameSlice.reducer;
